import { Check, Minus } from 'lucide-react';
import { forwardRef, useEffect, useRef, type ComponentType, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { mergeRefs } from './native';

type BoxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

const BOX = 'peer h-[18px] w-[18px] shrink-0 cursor-pointer appearance-none border border-slate-300 bg-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100';
const GLYPH = 'pointer-events-none absolute inset-0 m-auto opacity-0 transition-opacity';

/** A checkbox Bulk draws: a real input underneath, a Tiles-square on top. */
export const Checkbox = forwardRef<HTMLInputElement, BoxProps & { indeterminate?: boolean }>(function Checkbox({ className, indeterminate, ...props }, ref) {
  const own = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (own.current) own.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <input ref={mergeRefs(ref, own)} type="checkbox" className={cn(BOX, 'rounded-[6px] checked:border-ink checked:bg-ink indeterminate:border-ink indeterminate:bg-ink')} {...props} />
      <Check className={cn(GLYPH, 'h-3 w-3 text-white peer-checked:opacity-100 peer-indeterminate:opacity-0')} strokeWidth={3.5} aria-hidden />
      <Minus className={cn(GLYPH, 'h-3 w-3 text-white peer-indeterminate:opacity-100')} strokeWidth={3.5} aria-hidden />
    </span>
  );
});

/** A radio Bulk draws. Same input, round, with a dot. */
export const Radio = forwardRef<HTMLInputElement, BoxProps>(function Radio({ className, ...props }, ref) {
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <input ref={ref} type="radio" className={cn(BOX, 'rounded-full checked:border-ink checked:border-[5.5px]')} {...props} />
    </span>
  );
});

function ChoiceRow({ control, label, description, disabled, className }: { control: ReactNode; label: ReactNode; description?: ReactNode; disabled?: boolean; className?: string }) {
  return (
    <label className={cn('flex items-start gap-2.5 text-sm', disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-ink', className)}>
      <span className="mt-px">{control}</span>
      <span className="min-w-0">
        <span className="block font-medium leading-[18px]">{label}</span>
        {description ? <span className={cn('mt-0.5 block text-xs', disabled ? 'text-slate-400' : 'text-ink-muted')}>{description}</span> : null}
      </span>
    </label>
  );
}

export const CheckboxField = forwardRef<HTMLInputElement, BoxProps & { label: ReactNode; description?: ReactNode; indeterminate?: boolean; rowClassName?: string }>(function CheckboxField({ label, description, rowClassName, className, indeterminate, ...props }, ref) {
  return <ChoiceRow className={rowClassName} disabled={props.disabled} label={label} description={description} control={<Checkbox ref={ref} className={className} indeterminate={indeterminate} {...props} />} />;
});

export const RadioField = forwardRef<HTMLInputElement, BoxProps & { label: ReactNode; description?: ReactNode; rowClassName?: string }>(function RadioField({ label, description, rowClassName, className, ...props }, ref) {
  return <ChoiceRow className={rowClassName} disabled={props.disabled} label={label} description={description} control={<Radio ref={ref} className={className} {...props} />} />;
});

export interface ChoiceOption<T extends string = string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  disabled?: boolean;
}

type GroupProps<T extends string> = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'children'> & {
  options: readonly ChoiceOption<T>[];
  /** Only needed when the group is driven by state rather than by a form. */
  value?: T;
  label?: ReactNode;
};

/**
 * A row of mutually exclusive choices that reads as one control. Each choice is a
 * real radio, so `{...register(name)}` drives it the way it drives any other field.
 *
 * Every choice holds an empty line per label, drawn from `data-label` so no text is
 * repeated, which makes each prefer the widest label's width: the choices share the
 * row equally while that fits. Each can still shrink to its own label, kept on one
 * line, and past that whole choices move onto another row instead of overlapping.
 */
export function SegmentedControl<T extends string>({ options, value, className, label, ...props }: GroupProps<T>) {
  const widths = options.map((option) =>
    typeof option.label === 'string' ? <span key={option.value} aria-hidden data-label={option.label} className="block h-0 overflow-hidden after:content-[attr(data-label)]" /> : null,
  );
  return (
    <div role="group" aria-label={typeof label === 'string' ? label : undefined} className={cn('inline-flex w-full flex-wrap gap-1 rounded-[var(--radius-control)] bg-slate-100 p-1', className)}>
      {options.map((option) => (
        <label
          key={option.value}
          className={cn(
            'press relative min-w-min flex-1 rounded-[8px] px-3 py-1.5 text-center text-sm font-medium transition',
            option.disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-ink-muted hover:text-ink',
            'has-[:checked]:bg-white has-[:checked]:text-ink has-[:checked]:shadow-sm',
            'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-slate-400',
          )}
        >
          <input type="radio" className="sr-only" value={option.value} disabled={option.disabled} {...(value === undefined ? {} : { checked: value === option.value })} {...props} />
          <span className="whitespace-nowrap">{option.label}</span>
          {widths}
        </label>
      ))}
    </div>
  );
}

/** The same group drawn as tiles, for choices that carry a glyph or a sentence. */
export function RadioCards<T extends string>({ options, value, className, label, ...props }: GroupProps<T>) {
  return (
    <div role="group" aria-label={typeof label === 'string' ? label : undefined} className={cn('grid gap-2', className)}>
      {options.map((option) => {
        const Icon = option.icon;
        return (
          <label
            key={option.value}
            className={cn(
              'press flex flex-col items-center gap-1 rounded-[var(--radius-control)] border border-line px-2 py-2.5 text-center text-xs font-medium transition',
              option.disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-ink-muted hover:border-slate-300 hover:bg-slate-50',
              'has-[:checked]:border-ink has-[:checked]:bg-ink has-[:checked]:text-white',
              'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-slate-300 has-[:focus-visible]:ring-offset-1',
            )}
          >
            <input type="radio" className="sr-only" value={option.value} disabled={option.disabled} {...(value === undefined ? {} : { checked: value === option.value })} {...props} />
            {Icon ? <Icon className="h-5 w-5" aria-hidden /> : null}
            {option.label}
            {option.description ? <span className="text-[11px] font-normal opacity-80">{option.description}</span> : null}
          </label>
        );
      })}
    </div>
  );
}
